import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Image } from 'react-bootstrap';
import { ExpandableMenuStack } from 'Components/molecules/ExpandableMenuStack';
import { DropdownComponent } from 'Components/molecules';
import ChipMultiSelect, { OptionType } from 'Components/molecules/ChipMultiSelect/ChipMultiSelect';
import DeleteIcon from 'Assets/icons/DeleteIcon.svg';
import { listTagSpecs } from 'Apis/library';
import { RootState } from 'Rdx/store';
import { TagSpecDto } from '@ampsec/platform-client';
import { ADD_TAG_FILTER, TAG_FILTERS, TAG_FILTERS_CAPTION } from 'Core-utils/constants/constants';
import { TagFilterBuckets } from '../AgGridEngagementFlow/flowSpecTriggerTags.util';
import TagFilterCollapsedCaption from './TagFilterCollapsedCaption';
import { tagNameToChipOption } from './tagFilterChipOption.util';
import {
    TagFilterBucketKey,
    TagFilterRow,
    TAG_FILTER_TYPE_DROPDOWN,
    bucketToDropdownValue,
    bucketsToRows,
    buildGroupedTagFilterCaptionGroups,
    dropdownValueToBucket,
    rowsToBuckets,
} from './tagFilterUi.constants';
import './styles.scss';

type TagSpecOption = {
    id: string;
    name: string;
};

interface TagFiltersExpandableProps {
    tagBuckets: TagFilterBuckets;
    onTagBucketsChange: (buckets: TagFilterBuckets) => void;
}

function parseTagSpecs(res: unknown): TagSpecOption[] {
    const body = (res as { data?: unknown })?.data;
    let list: TagSpecDto[] = [];

    if (Array.isArray(body)) {
        list = body as TagSpecDto[];
    } else {
        const nested = (body as { data?: unknown })?.data;
        if (Array.isArray(nested)) {
            list = nested as TagSpecDto[];
        }
    }

    const normalized = list.map((item) => (item as TagSpecDto & { tagSpec?: TagSpecDto }).tagSpec ?? item);

    return normalized
        .map((spec) => ({
            id: spec.id,
            name: spec.name,
        }))
        .filter((spec): spec is TagSpecOption => Boolean(spec.id && spec.name));
}

function mapTagIdsToOptions(
    ids: string[],
    tagSpecOptions: OptionType[],
    tagSpecIdToName: Record<string, string>,
): OptionType[] {
    return ids.map((id) => {
        const existing = tagSpecOptions.find((option) => option.value === id);
        if (existing) {
            return existing;
        }

        const name = tagSpecIdToName[id] ?? id;
        return tagNameToChipOption(id, name);
    });
}

const TagFiltersExpandable = ({ tagBuckets, onTagBucketsChange }: TagFiltersExpandableProps) => {
    const tid = useSelector((state: RootState) => state.auth.selectedTenant?.id) as string | undefined;
    const [tagSpecs, setTagSpecs] = useState<TagSpecOption[]>([]);
    const [rows, setRows] = useState<TagFilterRow[]>(() => bucketsToRows(tagBuckets));
    const [nextRowId, setNextRowId] = useState(() => Math.max(0, ...bucketsToRows(tagBuckets).map((row) => row.id)) + 1);
    const lastEmittedBucketsRef = useRef(JSON.stringify(tagBuckets));

    useEffect(() => {
        const serialized = JSON.stringify(tagBuckets);
        if (serialized === lastEmittedBucketsRef.current) {
            return;
        }
        lastEmittedBucketsRef.current = serialized;
        const synced = bucketsToRows(tagBuckets);
        setRows(synced);
        setNextRowId(Math.max(0, ...synced.map((row) => row.id), 0) + 1);
    }, [tagBuckets]);

    useEffect(() => {
        let cancelled = false;

        async function fetchTagSpecs() {
            if (!tid) {
                if (!cancelled) {
                    setTagSpecs([]);
                }
                return;
            }

            try {
                const res = await listTagSpecs({ tid });
                if (!cancelled) {
                    setTagSpecs(parseTagSpecs(res));
                }
            } catch {
                if (!cancelled) {
                    setTagSpecs([]);
                }
            }
        }

        fetchTagSpecs();

        return () => {
            cancelled = true;
        };
    }, [tid]);

    const tagSpecOptions = useMemo<OptionType[]>(
        () => tagSpecs.map((tagSpec) => tagNameToChipOption(tagSpec.id, tagSpec.name)),
        [tagSpecs],
    );

    const tagSpecIdToName = useMemo(() => {
        const lookup: Record<string, string> = {};
        tagSpecs.forEach((tagSpec) => {
            lookup[tagSpec.id] = tagSpec.name;
        });
        return lookup;
    }, [tagSpecs]);

    const captionGroups = useMemo(
        () => buildGroupedTagFilterCaptionGroups(rows, tagSpecIdToName),
        [rows, tagSpecIdToName],
    );

    const collapsedCaption = useMemo(
        () => <TagFilterCollapsedCaption groups={captionGroups} />,
        [captionGroups],
    );

    const pushRows = (updatedRows: TagFilterRow[]) => {
        const buckets = rowsToBuckets(updatedRows);
        lastEmittedBucketsRef.current = JSON.stringify(buckets);
        setRows(updatedRows);
        onTagBucketsChange(buckets);
    };

    const hasIncompleteRow = rows.some((row) => !row.bucket);
    const usedFilterTypes = new Set(
        rows.map((row) => row.bucket).filter((bucket): bucket is TagFilterBucketKey => Boolean(bucket)),
    );
    const allFilterTypesAdded = TAG_FILTER_TYPE_DROPDOWN.every((item) => usedFilterTypes.has(item.bucket));
    const canAddRow = !hasIncompleteRow && !allFilterTypesAdded;

    const addRow = () => {
        if (!canAddRow) {
            return;
        }

        pushRows([...rows, { id: nextRowId, bucket: '', tagIds: [] }]);
        setNextRowId((current) => current + 1);
    };

    const deleteRow = (rowId: number) => {
        pushRows(rows.filter((row) => row.id !== rowId));
    };

    const handleFilterTypeChange = (rowId: number, selectedValue: string) => {
        const bucket = dropdownValueToBucket(selectedValue);
        pushRows(
            rows.map((row) => {
                if (row.id !== rowId) {
                    return row;
                }
                return { ...row, bucket, tagIds: row.tagIds };
            }),
        );
    };

    const handleTagsChange = (rowId: number, selected: OptionType[]) => {
        pushRows(
            rows.map((row) => {
                if (row.id !== rowId) {
                    return row;
                }
                return { ...row, tagIds: selected.map((option) => option.value) };
            }),
        );
    };

    const getDropdownItemsForRow = (rowId: number) => {
        const usedBuckets = new Set(
            rows.filter((row) => row.id !== rowId && row.bucket).map((row) => row.bucket as string),
        );

        return TAG_FILTER_TYPE_DROPDOWN.filter((item) => !usedBuckets.has(item.bucket)).map((item) => ({
            id: String(item.id),
            label: item.value,
            value: item.value,
        }));
    };

    return (
        <div className="tag-filters-expandable">
            <ExpandableMenuStack
                header={TAG_FILTERS}
                caption={[]}
                collapsedCaption={captionGroups.length > 0 ? collapsedCaption : undefined}
                color="vizualization-green-01"
                defaultCaption={TAG_FILTERS_CAPTION}
            >
                <>
                    {rows.map((row) => {
                        const selectedOptions = mapTagIdsToOptions(row.tagIds, tagSpecOptions, tagSpecIdToName);

                        return (
                            <div key={row.id} className="d-flex flex-row w-100 gap-2 mb-2 tag-filters-expandable__row">
                                <div className="tag-filters-expandable__dropdown-container">
                                    <DropdownComponent
                                        value={bucketToDropdownValue(row.bucket)}
                                        dropDownItems={getDropdownItemsForRow(row.id)}
                                        onChange={(selectedValue) =>
                                            handleFilterTypeChange(row.id, selectedValue || '')
                                        }
                                    />
                                </div>
                                <div className="tag-filters-expandable__select-container">
                                    <ChipMultiSelect
                                        id={`tag-filters-row-${row.id}`}
                                        selected={selectedOptions}
                                        onChange={(selected) =>
                                            handleTagsChange(row.id, selected as OptionType[])
                                        }
                                        placeholder="Select tags"
                                        options={tagSpecOptions}
                                    />
                                </div>
                                <Image
                                    src={DeleteIcon}
                                    className="tag-filters-expandable__delete-icon"
                                    onClick={() => deleteRow(row.id)}
                                    data-testid={`delete-tag-filter-row-${row.id}`}
                                />
                            </div>
                        );
                    })}
                    <Button
                        variant="body5 border-0 text-vizualization-green-100 d-flex justify-content-end align-items-center p-0 tag-filters-expandable__add-filter"
                        onClick={addRow}
                        disabled={!canAddRow}
                        title={
                            allFilterTypesAdded
                                ? 'All filter types are already in use'
                                : hasIncompleteRow
                                  ? 'Choose a filter type before adding another row'
                                  : undefined
                        }
                    >
                        {ADD_TAG_FILTER}
                    </Button>
                </>
            </ExpandableMenuStack>
        </div>
    );
};

export default TagFiltersExpandable;
